import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { firmsAction } from 'common/actions';
import { useStore } from 'common/store';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import { SecondaryNavigationActionButton } from 'components/ui';
import type { SecondaryNavigationButtonControlConfig } from 'context/FirmBaseLayoutContext';
import LayoutContext from 'context/LayoutContext';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext/CompanyDashboardContext';
import { updateUserState } from 'dashboard409a/states/features/user/userSlice';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import UserProfile from 'pages/UserProfile/UserProfile';

const AccountSettings = () => {
  const { setSecondaryNavActionsNode, updatePageActions } = useCompanyDashboardContext();
  const [saveAction, setSaveAction] = useState<SecondaryNavigationButtonControlConfig | null>(null);
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const reduxDispatch = useDispatch<AppDispatchType>();
  const reduxUser = useSelector((state: RootStateType) => state.user);

  useEffect(() => {
    const firmId = reduxUser?.firms_permissions?.[0]?.id;
    if (firmId) {
      dispatch(firmsAction.setFirmId(firmId));
    }
  }, [reduxUser, dispatch]);

  const handleSaveSuccess = useCallback(
    (response: unknown) => {
      const updatedUser = response as typeof reduxUser;
      if (updatedUser?.profile?.picture_url) {
        reduxDispatch(
          updateUserState({
            ...reduxUser,
            profile: { ...reduxUser.profile, picture_url: updatedUser.profile.picture_url },
          })
        );
      }
    },
    [reduxDispatch, reduxUser]
  );

  const layoutContextValue = useMemo(
    () => ({ setPageActions: updatePageActions as unknown as LayoutContextValues['setPageActions'] }),
    [updatePageActions]
  );

  const saveButton = useMemo(() => {
    if (!saveAction) {
      return null;
    }

    return (
      <SecondaryNavigationActionButton
        data-testid={saveAction.key}
        disabled={saveAction.disabled}
        isSaving={saveAction.isSaving}
        onClick={saveAction.onClick}
        variant={saveAction.variant ?? 'positive'}>
        {saveAction.label}
      </SecondaryNavigationActionButton>
    );
  }, [saveAction]);

  useEffect(() => {
    setSecondaryNavActionsNode?.(saveButton);
    return () => {
      setSecondaryNavActionsNode?.(null);
    };
  }, [saveButton, setSecondaryNavActionsNode]);

  return (
    <LayoutContext.Provider value={layoutContextValue as never}>
      <UserProfile onSaveActionChange={setSaveAction} onSaveSuccess={handleSaveSuccess} />
    </LayoutContext.Provider>
  );
};

export default AccountSettings;
